import React from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTranslation } from "@/context/LanguageContext";
import type { RoomPresenceMember } from "@/lib/realtime/roomChannels";

const AVATAR_COLORS = [
  "bg-blue-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-violet-500",
  "bg-cyan-500",
  "bg-orange-500",
  "bg-teal-500",
];

function avatarColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash + userId.charCodeAt(i)) | 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function initial(member: RoomPresenceMember): string {
  if (member.name) return member.name.charAt(0).toUpperCase();
  if (member.email) return member.email.charAt(0).toUpperCase();
  return "?";
}

interface OnlineMembersStackProps {
  members: RoomPresenceMember[];
  maxVisible?: number;
}

export const OnlineMembersStack: React.FC<OnlineMembersStackProps> = ({
  members,
  maxVisible = 5,
}) => {
  const { t, isRtl } = useTranslation();
  if (!members || members.length === 0) return null;

  const visible = members.slice(0, maxVisible);
  const overflow = members.length - visible.length;

  const Bubble: React.FC<{ member: RoomPresenceMember }> = ({ member }) => (
    <div
      className={`relative inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold text-white ring-2 ring-background ${avatarColor(
        member.user_id,
      )}`}
      title={member.name}
    >
      {initial(member)}
      <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-background" />
    </div>
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center ${
            isRtl ? "flex-row-reverse" : ""
          }`}
          aria-label={t("room.online.tooltip") || "Online now"}
        >
          {visible.map((m, i) => (
            <span
              key={m.user_id}
              className={i === 0 ? "" : isRtl ? "-mr-2" : "-ml-2"}
            >
              <Bubble member={m} />
            </span>
          ))}
          {overflow > 0 && (
            <span
              className={`relative inline-flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground ring-2 ring-background ${
                isRtl ? "-mr-2" : "-ml-2"
              }`}
            >
              +{overflow}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-56 p-2 [--radius:1rem]"
      >
        <div className="px-2 pb-2 pt-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          {t("room.online.tooltip") || "Online now"} · {members.length}
        </div>
        <div className="max-h-64 space-y-1 overflow-y-auto scrollbar-hide">
          {members.map((m) => (
            <div
              key={m.user_id}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/40"
            >
              <Bubble member={m} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-medium text-foreground">
                  {m.name}
                </div>
                {m.email && (
                  <div className="truncate text-[10px] text-muted-foreground">
                    {m.email}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
